/**
 * BayCrossing — public form for guided walks across the Mont-Saint-Michel bay
 * ("Traversée de la baie"). Simple, non-transactional — the Prieuré contacts
 * back with dates and details.
 */
import { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import SeoHead from "@/components/SeoHead";
import Ornament from "@/components/Ornament";

const COPY = {
  fr: {
    hero: "Traversée de la baie",
    tagline: "Une marche guidée entre Vains et le Mont-Saint-Michel",
    intro:
      "Chaque année, le Prieuré organise plusieurs traversées de la baie du Mont-Saint-Michel, dans la tradition des pèlerinages médiévaux. La marche dure environ 3 à 4 heures, encadrée par un guide diplômé. Merci de nous indiquer votre intérêt et une date préférentielle — nous vous recontactons.",
    first_name: "Prénom",
    last_name: "Nom",
    email: "Email",
    phone: "Téléphone",
    country: "Pays",
    date: "Date préférentielle",
    participants: "Nombre de participants",
    notes: "Commentaires (mobilité réduite, groupe scolaire, etc.)",
    submit: "Envoyer ma demande",
    success: "Merci pour votre demande. Nous vous répondrons rapidement.",
    error: "Erreur — merci de réessayer.",
  },
  en: {
    hero: "Bay Crossing",
    tagline: "A guided walk between Vains and Mont-Saint-Michel",
    intro:
      "Each year the Priory organises several crossings of the Mont-Saint-Michel bay, in the tradition of medieval pilgrimages. The walk lasts 3 to 4 hours, led by a certified guide. Please indicate your interest and a preferred date — we'll get back to you.",
    first_name: "First name",
    last_name: "Last name",
    email: "Email",
    phone: "Phone",
    country: "Country",
    date: "Preferred date",
    participants: "Number of participants",
    notes: "Comments (reduced mobility, school group, etc.)",
    submit: "Send my request",
    success: "Thank you for your request. We'll respond promptly.",
    error: "Error — please try again.",
  },
};

export default function BayCrossing() {
  const { locale } = useI18n();
  const t = COPY[locale === "fr" ? "fr" : "en"];
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "FR", preferred_date: "", nb_participants: 1, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);

  const upd = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api.post("/bay-crossing", form);
      setOk(true);
    } catch (err) {
      setMsg(err?.response?.data?.detail || t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bay-crossing" style={{ background: "var(--parchment)" }}>
      <SeoHead
        titles={{ fr: "Traversée de la baie du Mont-Saint-Michel", en: "Crossing of the Bay of Mont-Saint-Michel" }}
        descriptions={{
          fr: "Traversée guidée des grèves de la baie du Mont-Saint-Michel au départ du Prieuré saint Léonard de Vains : marche accompagnée vers le Mont, dans les pas des pèlerins des chemins de Saint-Michel, en Normandie.",
          en: "Guided crossing of the strands of the Bay of Mont-Saint-Michel from the Priory of Saint Léonard in Vains: an accompanied walk to the Mont, in the footsteps of the pilgrims of the Saint-Michel paths, in Normandy.",
        }}
        keywords="traversée de la baie, Mont-Saint-Michel, traversée des grèves, guide baie du Mont-Saint-Michel, chemins de Saint-Michel, pèlerinage, marche, Vains, Genêts, Normandie"
      />
      <section className="py-14 px-6 text-center border-b" style={{ borderColor: "var(--gold)" }}>
        <Ornament />
        <h1 className="font-heading text-4xl sm:text-5xl mt-4" style={{ color: "var(--ink)" }}>{t.hero}</h1>
        <p className="font-body italic text-base mt-3" style={{ color: "var(--muted)" }}>{t.tagline}</p>
      </section>

      <section className="max-w-2xl mx-auto px-6 py-12">
        <p className="font-body text-base leading-relaxed mb-8" style={{ color: "var(--ink)" }}>{t.intro}</p>

        {ok ? (
          <div className="bg-white border p-8 text-center" style={{ borderColor: "var(--gold)" }} data-testid="bay-success">
            <h2 className="font-heading text-2xl mb-2" style={{ color: "var(--ink)" }}>
              {locale === "fr" ? "Merci !" : "Thank you!"}
            </h2>
            <p className="font-body" style={{ color: "var(--muted)" }}>{t.success}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border p-8 space-y-4" style={{ borderColor: "var(--border)" }} data-testid="bay-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label={t.first_name} required value={form.first_name} onChange={upd("first_name")} testid="bay-first-name" />
              <F label={t.last_name} required value={form.last_name} onChange={upd("last_name")} testid="bay-last-name" />
              <F label={t.email} type="email" required value={form.email} onChange={upd("email")} testid="bay-email" />
              <F label={t.phone} value={form.phone} onChange={upd("phone")} testid="bay-phone" />
              <F label={t.country} value={form.country} onChange={upd("country")} testid="bay-country" />
              <F label={t.date} type="date" value={form.preferred_date} onChange={upd("preferred_date")} testid="bay-date" />
              <F label={t.participants} type="number" min="1" max="50" value={form.nb_participants} onChange={upd("nb_participants")} testid="bay-nb" />
            </div>
            <label className="block">
              <span className="block text-xs font-body uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{t.notes}</span>
              <textarea data-testid="bay-notes" className="w-full px-3 py-2 border font-body" rows={3} style={{ borderColor: "var(--border)" }} value={form.notes} onChange={upd("notes")} />
            </label>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="bay-submit">
              {busy ? "…" : t.submit}
            </button>
            {msg && <p className="text-sm" style={{ color: "var(--cinnabar)" }} data-testid="bay-error">{msg}</p>}
          </form>
        )}
      </section>
    </div>
  );
}

function F({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-body uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{label}</span>
      <input data-testid={testid} className="w-full px-3 py-2 border font-body" style={{ borderColor: "var(--border)" }} {...rest} />
    </label>
  );
}
